import {
  complement,
  compose,
  filter,
  isNil,
  join,
  map,
  pluck,
  propEq,
  pipe,
  equals,
  always,
  cond,
  values,
  T,
  ifElse,
  gt,
  prop,
  head,
  propOr,
  keys,
  where,
  any,
  zipObj,
  repeat,
  find,
  sum,
  isEmpty,
  pick,
  both,
  length,
  mergeAll,
  defaultTo,
} from 'ramda';
import moment from 'moment';
import qs from 'query-string';
import { camelizeKeys, decamelizeKeys } from 'humps';

import {
  DEFAULT_DATE_FILTER_DATA,
  DEFAULT_FILTER_DATA,
  DEFAULT_FILTER_LABEL,
  FilterNames,
  QueryParamNames,
} from './filters.constants';
import messages from './filters.messages';
import { QUERY_DATE_FORMAT } from '../../shared/utils/date';

const DATE_FORMAT = 'DD MMM';

const filterTruthful = filter(propEq('value', true));

const getDefaultMessage = value => ({
  ...messages.defaultMessage,
  values: { value },
});

const getLocationLabelValue = (market, regionNames) => {
  if (market.value) {
    return market.name;
  }

  if (regionNames.length) {
    const marketName = head(regionNames);
    return `${marketName} (${regionNames.length})`;
  }

  return DEFAULT_FILTER_LABEL;
};

export const mapMarketsAndRegionsNames = map(market => {
  const regionNames = compose(
    pluck('name'),
    Object.values,
    filterTruthful
  )(market.regions);

  return getLocationLabelValue(market, regionNames);
});

export const getLocationFilterLabel = pipe(
  mapMarketsAndRegionsNames,
  Object.values,
  head,
  ifElse(both(complement(isNil), complement(isEmpty)), getDefaultMessage, always(DEFAULT_FILTER_LABEL))
);

export const getDateLabel = pipe(
  values,
  filter(complement(isNil)),
  cond([
    [
      compose(
        gt(2),
        prop('length')
      ),
      always(DEFAULT_FILTER_LABEL),
    ],
    [
      ([start, end]) => equals(start.valueOf(), end.valueOf()),
      compose(
        getDefaultMessage,
        date => moment(date).format(DATE_FORMAT),
        prop(0)
      ),
    ],
    [
      T,
      pipe(
        map(date => moment(date).format(DATE_FORMAT)),
        join(' - '),
        getDefaultMessage
      ),
    ],
  ])
);

export const getGuestsLabel = pipe(
  values,
  sum,
  ifElse(equals(0), always(DEFAULT_FILTER_LABEL), count => ({
    ...messages.guests,
    values: { count },
  }))
);

export const getActivitiesLabel = pipe(
  defaultTo({}),
  filter(propEq('value', true)),
  keys,
  length,
  ifElse(equals(0), always(DEFAULT_FILTER_LABEL), count => ({
    ...messages.activities,
    values: { count },
  }))
);

export const getLabelTransformFn = cond([
  [equals(FilterNames.LOCATION), always(getLocationFilterLabel)],
  [equals(FilterNames.DATE), always(getDateLabel)],
  [equals(FilterNames.GUESTS), always(getGuestsLabel)],
  [equals(FilterNames.ACTIVITIES), always(getActivitiesLabel)],
  [T, always(null)],
]);

const getLocationFilterQuery = locationFilter => {
  const { data } = locationFilter;
  const market = head(values(data));
  const selectedRegions = pipe(
    filter(propEq('value', true)),
    keys
  )(propOr([], 'regions', market));

  const marketValue = prop('value', market);
  const selectedMarketId = marketValue ? head(keys(data)) : undefined;
  const selectedRegionsIds = marketValue ? [] : selectedRegions;

  return {
    [QueryParamNames.MARKET]: selectedMarketId,
    [QueryParamNames.REGION]: selectedRegionsIds,
  };
};

const getDateFilterQuery = dateFilter => {
  const { data } = dateFilter;
  const { from, to } = data;

  if (!from || !to) {
    return '';
  }

  const [dateStart, dateEnd] = [moment(from), moment(to)].map(d => d.format(QUERY_DATE_FORMAT));

  return { dateStart, dateEnd };
};

const getGuestsFilterQuery = pipe(
  prop('data'),
  filter(complement(equals(0)))
);

const getActivitiesFilterQuery = pipe(
  prop('data'),
  filter(propEq('value', true)),
  keys,
  ifElse(isEmpty, always(undefined), v => ({ [QueryParamNames.ACTIVITIES]: v }))
);

export const getFiltersQuery = filtersData => {
  const queries = {
    ...getLocationFilterQuery(filtersData.location),
    ...getDateFilterQuery(filtersData.date),
    ...getGuestsFilterQuery(filtersData.guests),
    ...getActivitiesFilterQuery(filtersData.activities),
  };

  const decamelized = decamelizeKeys(queries);

  const query = qs.stringify(decamelized, { arrayFormat: 'bracket' });
  return isEmpty(query) ? '' : `?${query}`;
};

export const getFiltersQueryValues = query => {
  const parsed = qs.parse(query, { arrayFormat: 'bracket' });
  const camelized = camelizeKeys(parsed);

  const marketId = camelized[QueryParamNames.MARKET];
  const regionsIds = camelized[QueryParamNames.REGION];
  const activitiesIds = camelized[QueryParamNames.ACTIVITIES];

  const guests = pipe(
    pick([QueryParamNames.ADULT, QueryParamNames.CHILD, QueryParamNames.INFANT]),
    map(parseInt)
  )(camelized);

  const dates = pipe(
    pick([QueryParamNames.DATE_START, QueryParamNames.DATE_END]),
    map(d => moment(d, QUERY_DATE_FORMAT).format())
  )(camelized);

  return {
    marketId,
    regionsIds,
    guests,
    dates,
    activitiesIds,
  };
};

const getRestoredLocationFilterState = dataNeededToRestore => {
  if (!dataNeededToRestore || dataNeededToRestore.marketId === undefined) {
    return {};
  }

  const defaultSelectedRegionValue = { value: true, name: dataNeededToRestore.marketName };
  const regionsIds = dataNeededToRestore.marketRegionsIds
    ? dataNeededToRestore.marketRegionsIds
    : dataNeededToRestore.regionsIds;

  return {
    [dataNeededToRestore.marketId]: {
      name: dataNeededToRestore.marketName,
      value: dataNeededToRestore.marketRegionsIds ? true : !dataNeededToRestore.regionsIds.length,
      regions: zipObj(regionsIds, repeat(defaultSelectedRegionValue, regionsIds.length)),
    },
  };
};

const getRestoredActivitiesFilterState = dataNeededToRestore => {
  if (!dataNeededToRestore) {
    return DEFAULT_FILTER_DATA;
  }

  return pipe(
    map(v => ({ [v]: { value: true } })),
    mergeAll
  )(dataNeededToRestore);
};

export const getRestoredFiltersState = dataNeededToRestoreFiltersState => {
  const guests = dataNeededToRestoreFiltersState[FilterNames.GUESTS] || DEFAULT_FILTER_DATA;
  const activities = dataNeededToRestoreFiltersState[FilterNames.ACTIVITIES];
  const dates = dataNeededToRestoreFiltersState[FilterNames.DATE] || DEFAULT_DATE_FILTER_DATA;

  const restoredData = {
    [FilterNames.LOCATION]: getRestoredLocationFilterState(dataNeededToRestoreFiltersState[FilterNames.LOCATION]),
    [FilterNames.DATE]: {
      from: dates[QueryParamNames.DATE_START] || null,
      to: dates[QueryParamNames.DATE_END] || null,
    },
    [FilterNames.GUESTS]: guests,
    [FilterNames.ACTIVITIES]: getRestoredActivitiesFilterState(activities),
  };

  return {
    [FilterNames.LOCATION]: {
      name: FilterNames.LOCATION,
      label: getLocationFilterLabel(restoredData[FilterNames.LOCATION]),
      data: restoredData[FilterNames.LOCATION],
    },
    [FilterNames.DATE]: {
      name: FilterNames.DATE,
      label: getDateLabel(restoredData[FilterNames.DATE]),
      data: restoredData[FilterNames.DATE],
    },
    [FilterNames.GUESTS]: {
      name: FilterNames.GUESTS,
      label: getGuestsLabel(restoredData[FilterNames.GUESTS]),
      data: restoredData[FilterNames.GUESTS],
    },
    [FilterNames.ACTIVITIES]: {
      name: FilterNames.ACTIVITIES,
      label: getActivitiesLabel(restoredData[FilterNames.ACTIVITIES]),
      data: restoredData[FilterNames.ACTIVITIES],
    },
  };
};

const getMarketFromFromRestoredLocationFilterState = (marketsWithRegions, marketId, regionsIds) => {
  if (marketId) {
    return find(propEq('id', marketId), marketsWithRegions);
  }

  const regionId = head(regionsIds);
  return find(where({ regions: any(propEq('id', regionId)) }), marketsWithRegions);
};

export const getDataNeededToRestoreLocationFilterState = (marketsWithRegions, marketId, regionsIds = []) => {
  const market = getMarketFromFromRestoredLocationFilterState(marketsWithRegions, marketId, regionsIds);

  return {
    marketId: prop('id', market),
    marketName: prop('name', market),
    marketRegionsIds: regionsIds.length ? undefined : pluck('id', propOr([], 'regions', market)),
    regionsIds,
  };
};
